// Pure helpers for the tray manager - no React/axios imports so they can be
// unit-tested directly.

// ---------- split slots (half-size micro trays) ----------
// A bench cell holds either one full tray object, or a split container for
// two half-size micro trays: { split: true, halves: [trayOrNull, trayOrNull] }
export const isSplitSlot = (s) => !!(s && s.split === true);

export const slotTrays = (s) =>
  isSplitSlot(s)
    ? (s.halves || []).filter((h) => h && h.cropType)
    : (s && s.cropType ? [s] : []);

export const isSlotOccupied = (s) => slotTrays(s).length > 0;

export const halfLabel = (half) => (half === 0 ? 'A' : 'B');

export const isTrayOccupied = (t) => !!(t && t.cropType);

export const isBenchEmpty = (bench) =>
  bench.layers.every((layer) => layer.every((slot) => !isSlotOccupied(slot)));

export const findFirstEmptyCell = (bench) => {
  for (let layer = 0; layer < bench.layers.length; layer++) {
    for (let tray = 0; tray < bench.layers[layer].length; tray++) {
      if (!isSlotOccupied(bench.layers[layer][tray])) return { layer, tray };
    }
  }
  return null;
};

// ---------- dates ----------
export const parseDMY = (dmy) => {
  if (!dmy || !/^\d{2}-\d{2}-\d{4}$/.test(dmy)) return null;
  const [dd, mm, yyyy] = dmy.split('-');
  const dt = new Date(`${yyyy}-${mm}-${dd}T12:00:00Z`);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

export const daysSince = (dmy) => {
  const seededAt = parseDMY(dmy);
  if (!seededAt) return null;
  return Math.max(0, Math.floor((Date.now() - seededAt.getTime()) / 86400000));
};

// ---------- formatting ----------
export const formatBenchName = (name) =>
  name.replace(/([A-Z])/g, ' $1').replace(/([a-z])(\d)/g, '$1 $2').trim().replace(/^./, c => c.toUpperCase());

// ---------- harvest readiness ----------
export function resolveHarvestInfo(cropName, seedInventory) {
  const rec = seedInventory?.[cropName];

  // DB overrides take priority
  if (rec?.harvestType === 'repeat') return { type: 'repeat',   targetDays: null };
  if (rec?.harvestType === 'micro')  return { type: 'micro',    targetDays: rec.harvestDays ?? 14 };
  if (rec?.harvestType === 'standard') return { type: 'standard', targetDays: rec.harvestDays ?? 30 };
  if (rec?.harvestDays != null)        return { type: 'standard', targetDays: rec.harvestDays };

  // Fall back to keyword detection (unchanged from original)
  const REPEAT_CROPS = ['basil', 'chives', 'mint'];
  const MICRO_CROPS  = ['cress', 'pea shoot', 'pea shoots', 'peashoots', 'peashoot'];
  const key = (cropName || '').toLowerCase().trim();
  if (REPEAT_CROPS.some((r) => key.includes(r))) return { type: 'repeat',   targetDays: null };
  if (MICRO_CROPS.some((m)  => key.includes(m))) return { type: 'micro',    targetDays: 14   };
  return                                                  { type: 'standard', targetDays: 30   };
}

export function calcBoxReadiness(box, allTrays, seedInventory) {
  const items = (box.contents || []).filter(Boolean).map((cropName) => {
    // Match trays whose cropType contains or equals this content item (case-insensitive)
    const key = cropName.toLowerCase().trim();
    const trays = allTrays.filter(
      (r) => r.tray?.cropType?.toLowerCase().trim() === key ||
             r.tray?.cropType?.toLowerCase().includes(key)
    );

    if (!trays.length) {
      return { cropName, trays: [], bestTray: null, daysUntilReady: null, status: 'missing' };
    }

    const { type, targetDays } = resolveHarvestInfo(cropName, seedInventory);

    if (type === 'repeat') {
      return { cropName, trays, bestTray: trays[0], daysUntilReady: 0, status: 'ready' };
    }

    // Find the tray closest to ready (highest pct)
    const withPct = trays.map((r) => ({
      ...r,
      daysLeft: Math.max(0, targetDays - r.ageDays),
    })).sort((a, b) => a.daysLeft - b.daysLeft);

    const best = withPct[0];
    const daysLeft = best.daysLeft;

    const status =
      daysLeft === 0                      ? 'ready'
      : daysLeft <= 7                     ? 'soon'
      :                                     'growing';

    return {
      cropName,
      trays: withPct,
      bestTray: best,
      daysUntilReady: daysLeft,
      targetDays,
      status,
    };
  });

  const anyMissing = items.some((i) => i.status === 'missing');
  const nonRepeatItems = items.filter((i) => i.status !== 'missing');

  let overallDays = null;
  if (!anyMissing && nonRepeatItems.length) {
    overallDays = Math.max(...nonRepeatItems.map((i) => i.daysUntilReady ?? 0));
  }

  const readyDate = overallDays != null
    ? new Date(Date.now() + overallDays * 86400000)
    : null;

  const allReady = !anyMissing && overallDays === 0;

  return { items, overallDays, readyDate, allReady, anyMissing };
}

// ---------- harvest forecast ----------
// Buckets growing trays into blocks of days by when they'll hit their harvest
// target, per crop. `rows` are the All-trays rows ({ tray, half, ageDays,
// harvestType, targetDays }). Half trays count as 0.5. Column 0 is "ready now"
// (at or past target), then `blocks` blocks of `blockDays`, then "later".
// Repeat-harvest crops have no ready date, so they're returned separately.
export function buildHarvestForecast(rows, { blockDays = 7, blocks = 4 } = {}) {
  const columns = [
    { key: 'now', label: 'Ready now', fromDay: null, toDay: 0 },
    ...Array.from({ length: blocks }, (_, b) => ({
      key: `b${b}`, fromDay: b * blockDays + 1, toDay: (b + 1) * blockDays,
    })),
    { key: 'later', label: 'Later', fromDay: blocks * blockDays + 1, toDay: null },
  ];

  const crops = new Map();
  const repeat = new Map();

  rows.forEach((row) => {
    const name = row.tray?.cropType;
    if (!name) return;
    const amount = row.half != null ? 0.5 : 1;

    if (row.harvestType === 'repeat') {
      repeat.set(name, (repeat.get(name) || 0) + amount);
      return;
    }

    const daysLeft = Math.max(0, (row.targetDays ?? 0) - (row.ageDays ?? 0));
    const col = daysLeft === 0 ? 0 : Math.min(blocks + 1, Math.floor((daysLeft - 1) / blockDays) + 1);

    if (!crops.has(name)) {
      crops.set(name, { cropName: name, cells: columns.map(() => ({ amount: 0, rows: [] })), total: 0 });
    }
    const crop = crops.get(name);
    crop.cells[col].amount += amount;
    crop.cells[col].rows.push({ ...row, daysLeft });
    crop.total += amount;
  });

  const cropList = [...crops.values()]
    .map((c) => {
      let running = 0;
      c.cumulative = c.cells.map((cell) => (running += cell.amount));
      c.cells.forEach((cell) => cell.rows.sort((a, b) => a.daysLeft - b.daysLeft));
      return c;
    })
    .sort((a, b) => b.total - a.total || a.cropName.localeCompare(b.cropName));

  const totals = columns.map((_, i) => cropList.reduce((sum, c) => sum + c.cells[i].amount, 0));

  return {
    columns,
    crops: cropList,
    totals,
    repeat: [...repeat.entries()].map(([cropName, amount]) => ({ cropName, amount }))
      .sort((a, b) => a.cropName.localeCompare(b.cropName)),
  };
}
